import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef } from "react";
import { useUserRole } from "@/hooks/use-user-role";

export const useTestUser = () => {
  const { user, logout } = useAuth();
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { data: userRole } = useUserRole();
  
  const query = useQuery({
    queryKey: ['test-user', user?.id, userRole],
    queryFn: async () => {
      if (!user?.id) return { isTestUser: false };
      
      const { data, error } = await supabase
        .from('profiles')
        .select('is_test_account, organization_id')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Error checking test user status:', error);
        return { isTestUser: false };
      }
      
      return {
        isTestUser: userRole === 'test_user' && data?.is_test_account === true,
        organizationId: data?.organization_id
      };
    },
    enabled: !!user?.id,
  });

  // Auto-logout test users after 1 hour
  useEffect(() => {
    if (query.data?.isTestUser) {
      // Clear any existing timer
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      
      // Set a timer for 1 hour (3600000 milliseconds)
      logoutTimerRef.current = setTimeout(() => {
        if (import.meta.env.DEV) console.log('Test user session expired after 1 hour, logging out...');
        logout();
      }, 3600000);
    } else {
      // Clear timer if user is not a test user
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    }

    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
    };
  }, [query.data?.isTestUser, logout]);

  return query;
};

export const useTestUserRestrictions = () => {
  const { data: testUserData } = useTestUser();
  
  return {
    isTestUser: testUserData?.isTestUser || false,
    canAccessBilling: !(testUserData?.isTestUser),
    canAccessUsers: !(testUserData?.isTestUser),
    isReadOnly: testUserData?.isTestUser || false,
  };
};